import { CheckIcon, CircleIcon, Loader2Icon, RotateCcwIcon, XIcon } from 'lucide-react'

import { cn } from 'cn'
import { Button } from '@ems/ui/components/button'
import { formatMoney } from '@ems/ui/lib/format'

import type {
  Estimate,
  EstimateLine,
  Settlement,
} from '#admin/features/exhibitor/onboarding/draft.ts'
import type { DeskStep } from '#admin/features/exhibitor/onboarding/run.ts'

export type RunState =
  | { phase: 'editing' }
  | { phase: 'running'; step: DeskStep }
  | { phase: 'failed'; step: DeskStep; message: string }

/**
 * The order on the right: what the exhibitor pays, and the one button that does everything. While
 * the desk runs, the button gives its place to the steps, so the operator sees where it is.
 */
export function StickySummary({
  estimate,
  currency,
  settlement,
  steps,
  run,
  problems,
  onComplete,
  title = 'Order summary',
}: {
  estimate: Estimate
  currency: string
  settlement: Settlement
  /** The steps of the run, with their labels. */
  steps: readonly { step: DeskStep; label: string }[]
  run: RunState
  /** How many fields stop the desk from sending. */
  problems: number
  onComplete: () => void
  /** The sponsor desk calls it the sponsor package summary. */
  title?: string
}) {
  const money = (amount: number) => formatMoney(amount, currency)
  const empty = estimate.stands.length === 0 && estimate.services.length === 0
  const at = run.phase === 'editing' ? -1 : steps.findIndex((item) => item.step === run.step)

  return (
    <aside
      aria-label={title}
      className="bg-card flex flex-col gap-4 self-start rounded-lg border border-(--border) p-4 md:p-6 lg:sticky lg:top-20"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">{title}</h2>
        <span className="bg-muted rounded-md px-2 py-0.5 font-mono text-xs">{currency}</span>
      </div>

      {/* One status region, so a screen reader hears the new total once, not each figure. */}
      <div role="status" aria-atomic="true" className="flex flex-col gap-3 text-sm">
        {empty ? (
          <p className="text-muted-foreground">Select a stand to see the price.</p>
        ) : (
          <>
            <Lines title="Stand rental" lines={estimate.stands} money={money} />
            <Lines title="Services" lines={estimate.services} money={money} />
            <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 border-t border-(--border) pt-3 tabular-nums">
              <dt className="text-muted-foreground">Subtotal</dt>
              <dd className="text-right">{money(estimate.subtotal)}</dd>
              <dt className="text-muted-foreground">Tax</dt>
              <dd className="text-right">{money(estimate.tax)}</dd>
              <dt className="text-base font-semibold">Estimated total</dt>
              <dd className="text-right text-base font-semibold">{money(estimate.total)}</dd>
            </dl>
            <p className="text-muted-foreground text-xs">
              {estimate.services.length === 0
                ? 'The invoice shows the final figures.'
                : 'Services can add installation fees and an on-site surcharge. The invoices show the final figures.'}
            </p>
          </>
        )}
      </div>

      <p className="bg-muted/50 rounded-md px-3 py-2 text-xs">
        {settlement === 'paid'
          ? 'The stands are marked paid now.'
          : 'The stands get an invoice to pay later.'}
        {estimate.services.length === 0 ? '' : ' The services always get their own invoice.'}
      </p>

      {run.phase === 'editing' ? (
        <div className="flex flex-col gap-2">
          <Button size="lg" disabled={problems > 0} onClick={onComplete}>
            Complete registration and issue all
          </Button>
          {problems > 0 ? (
            <p className="text-muted-foreground text-center text-xs">
              {`Complete ${problems} ${problems === 1 ? 'field' : 'fields'} to continue.`}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <ol aria-label="Progress" className="flex flex-col gap-2 text-sm">
            {steps.map((item, index) => (
              <StepRow
                key={item.step}
                label={item.label}
                state={
                  index < at
                    ? 'done'
                    : index > at
                      ? 'waiting'
                      : run.phase === 'failed'
                        ? 'failed'
                        : 'running'
                }
              />
            ))}
          </ol>
          {run.phase === 'failed' ? (
            <>
              <p role="alert" className="text-destructive text-sm">
                {run.message}
              </p>
              <Button onClick={onComplete}>
                <RotateCcwIcon />
                Try again from this step
              </Button>
              <p className="text-muted-foreground text-xs">
                The steps that are done are kept. Correct the field and try again.
              </p>
            </>
          ) : null}
        </div>
      )}
    </aside>
  )
}

function Lines({
  title,
  lines,
  money,
}: {
  title: string
  lines: readonly EstimateLine[]
  money: (amount: number) => string
}) {
  if (lines.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">{title}</h3>
      <ul className="flex flex-col gap-1.5">
        {lines.map((line) => (
          <li key={line.key} className="flex items-start justify-between gap-3">
            <span className="min-w-0">
              <span className="block truncate">{line.label}</span>
              <span className="text-muted-foreground block text-xs">{line.detail}</span>
            </span>
            <span className="tabular-nums">{money(line.amount)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

const STEP_ICON = {
  done: <CheckIcon className="size-3.5" />,
  running: <Loader2Icon className="size-3.5 motion-safe:animate-spin" />,
  failed: <XIcon className="size-3.5" />,
  waiting: <CircleIcon className="size-2" />,
}

/** One step of a run: done, running, failed or waiting. The registration desk uses it too. */
export function StepRow({
  label,
  state,
}: {
  label: string
  state: 'done' | 'running' | 'failed' | 'waiting'
}) {
  return (
    <li
      className={cn(
        'flex items-center gap-2.5',
        state === 'waiting' && 'text-muted-foreground',
        state === 'failed' && 'text-destructive',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex size-5 shrink-0 items-center justify-center rounded-full',
          state === 'done' && 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
          state === 'running' && 'bg-primary/10 text-primary',
          state === 'failed' && 'bg-destructive/10',
          state === 'waiting' && 'bg-muted',
        )}
      >
        {STEP_ICON[state]}
      </span>
      {label}
      <span className="sr-only">{`: ${state}`}</span>
    </li>
  )
}
